<script setup lang="ts">
// Vue
import { ref } from "vue";

// Dependencies
import { v4 as uuidv4 } from "uuid";

// Components
import InputLabel from "./InputLabel.vue";

const props = defineProps<{
  value: string | null;
  options: Array<string>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  isConnectedLeft?: boolean;
  tailwindWidth?: string;
  textSize?: string;
  errorMsg?: string;
  disabled?: boolean;
}>();

const emits = defineEmits(["update:value"]);

const isHidden = ref<boolean>(true);
const currentlyHoveredIndex = ref<number>(0);
const id = ref<string>(uuidv4());

const hide = () => {
  isHidden.value = true;
};
const clickOutside = ref({
  dontTarget: `${id.value}`,
  hidden: false,
  function: hide,
});

const toggleIsHidden = () => {
  if (props.disabled) return;
  isHidden.value = !isHidden.value;
  if (isHidden.value) return;
};

const nextItem = (event: any) => {
  if (event.keyCode !== 38 && event.keyCode !== 40) return;
  if (event.keyCode === 38) {
    if (currentlyHoveredIndex.value <= 0) return;
    currentlyHoveredIndex.value--;
  } else {
    if (currentlyHoveredIndex.value >= props.options.length - 1) return;
    currentlyHoveredIndex.value++;
  }

  const focusedElement = document.getElementById(`${id.value}-${currentlyHoveredIndex.value}`);

  focusedElement?.scrollIntoView({
    behavior: "smooth",
    block: "nearest",
    inline: "start",
  });
};

const handleNewSelected = (option: string) => {
  emits("update:value", option);
  isHidden.value = true;
};
</script>

<template>
  <div>
    <div class="flex w-full flex-col">
      <InputLabel
        :id="id"
        :label="label"
        :required="required" />
      <div class="relative">
        <button
          :id="id ? id : `input-${id}`"
          class="text-type-muted rounded-8 flex h-9 items-center justify-between gap-6 border p-2 pr-4 text-sm leading-none"
          :class="[
            isConnectedLeft ? 'rounded-l-none border-l-0' : '',
            tailwindWidth ? tailwindWidth : 'w-44',
            errorMsg ? 'border-app-red-500' : 'border-slate-300',
            disabled ? 'cursor-not-allowed bg-slate-200' : 'bg-slate-0 cursor-pointer',
          ]"
          aria-haspopup="listbox"
          :aria-expanded="!isHidden"
          :aria-controls="`${id}-listbox`"
          :aria-label="label || undefined"
          @keyup="nextItem"
          @keyup.enter="handleNewSelected(props.options[currentlyHoveredIndex])"
          @click="toggleIsHidden">
          <span
            class="w-full truncate text-start"
            :class="[textSize ? textSize : '', props.value ? 'text-text-color' : '']"
            @click.stop="toggleIsHidden"
            >{{ !props.value && placeholder ? placeholder : props.value }}</span
          >
          <font-awesome-icon
            v-if="isHidden"
            icon="fa-solid fa-chevron-down"
            class="absolute right-2"
            @click.stop="toggleIsHidden" />
          <font-awesome-icon
            v-else
            icon="fa-solid fa-chevron-up"
            class="absolute right-2"
            @click.stop="toggleIsHidden" />
        </button>
        <span
          v-if="errorMsg"
          class="text-app-red-700 text-xs"
          >{{ errorMsg }}</span
        >

        <Transition name="fade-top">
          <div
            v-if="!isHidden"
            :id="`${id}-listbox`"
            v-click-outside="clickOutside"
            role="listbox"
            :aria-label="label || undefined"
            class="rounded-8 absolute top-13 right-0 z-50 flex max-h-96 w-full flex-col overflow-y-auto bg-slate-100 shadow-xs">
            <div
              v-for="(option, index) in props.options"
              :id="`${id}-${index}`"
              :key="option"
              role="option"
              :aria-selected="option === props.value"
              class="flex w-full cursor-pointer gap-4 p-2 py-2 pl-5"
              :class="{ 'bg-slate-100': index === currentlyHoveredIndex }"
              @mouseenter="currentlyHoveredIndex = index"
              @click="handleNewSelected(option)">
              <span
                class="w-full truncate"
                :class="[textSize ? textSize : '']"
                >{{ option }}</span
              >
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>
